import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  InputNumber,
  DatePicker,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import MainLayout from "../../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../../services/definedConstant";
import { rightAction } from "../../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../../treasuryContractBooking.config";
import { BOM } from "../../../../services/definedMethod";

// Allotment Type: 1 = Competitive, 2 = Non-Competitive
const ALLOTMENT_TYPE_OPTIONS = [
  { label: "Competitive (1)", value: 1 },
  { label: "Non-Competitive (2)", value: 2 },
];

const CreateAuctionAllotment = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [auctionLineOptions, setAuctionLineOptions] = useState([]);
  const [bidOptions, setBidOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const auctionId = location.state;
  const { userData } = props;
  const { session } = userData || {};

  // ── Fetch template LOV on mount ───────────────────────────────────────────
  useEffect(() => {
    if (auctionId) fetchTemplate(auctionId);
    // bidTemplate(auctionId)
  }, [auctionId]);

  const bidTemplate =(id)=>{
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetAuctions}/auctions/${id}/bids/template`;  
    
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to load allotment template.",
        );
        setIsLoading(false);
        return;
      }

      const lines = (res?.lines || []).map((l) => ({
        label: `${l.tenorDays}-Day Line (ID: ${l.auctionLineId})`,
        value: l.auctionLineId,
      }));

      const bids = (res?.bids || []).map((b) => ({
        label: `Bid ID: ${b.id} — ${b.bidAmount?.toLocaleString("en-NG")}`,
        value: b.id,
      }));

      setAuctionLineOptions(lines);
      setBidOptions(bids);
      setIsLoading(false);
    });
  }
  
  const fetchTemplate = (id) => {
    setIsLoading(true);
    const body = { session };
    // const url = `${apiConfig.GetAuctions}/auctions/${id}/bids/template`;
    const url = `${apiConfig.GetAuctions}/auctions/${id}/allotments/template`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to load allotment template.",
        );
        setIsLoading(false);
        return;
      }

      const lines = (res?.lines || []).map((l) => ({
        label: `${l.tenorDays}-Day Line (ID: ${l.auctionLineId})`,
        value: l.auctionLineId,
      }));

      const bids = (res?.bidTypeOptions || []).map((b) => ({
        label: `${b.value}`,
        value: b.id,
      }));

      setAuctionLineOptions(lines);
      setBidOptions(bids);
      setIsLoading(false);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const body = {
      method: "POST",
      auctionLineId: values.auctionLineId,
      bidId: values.bidId,
      allottedAmount: values.allottedAmount,
      allottedRate: values.allottedRate,
      allotmentType: values.allotmentType,
      allotmentDate: values.allotmentDate?.format("YYYY-MM-DD"),
      session,
       locale:           "en",
      dateFormat:       "yyyy-MM-dd",
      // Optional fields — only include if filled
      ...(values.allotmentYield != null && {
        allotmentYield: values.allotmentYield,
      }),
      ...(values.allotmentPrice != null && {
        allotmentPrice: values.allotmentPrice,
      }),
      ...(values.settlementDate && {
        settlementDate: values.settlementDate?.format("YYYY-MM-DD"),
      }),
    };

    const url = `${apiConfig.GetAuctions}/auctions/${auctionId}/allotments`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            "Failed to create auction allotment.",
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(cnt.SUCCESS, "Auction allotment created successfully.");
      setIsLoading(false);
      navigate(-1);
    });
  };

  const Tip = ({ text, tip }) => (
    <span>
      {text}
      <Tooltip title={tip}>
        <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
      </Tooltip>
    </span>
  );

  return (
    <MainLayout pageItems={staticConfig.newAuction}>
      <TopContent pageTitle={labelConfig.auction} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 24, fontWeight: 600 }}>
              Create Auction Allotment
            </h3>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* Auction Line & Bid — required */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Auction Line"
                        tip="The auction line this allotment is being made against."
                      />
                    }
                    name="auctionLineId"
                    rules={[
                      { required: true, message: "Auction line is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={auctionLineOptions}
                      placeholder="Select Auction Line"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={<Tip text="Bid" tip="The bid being allotted." />}
                    name="bidId"
                    rules={[{ required: true, message: "Bid is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={ALLOTMENT_TYPE_OPTIONS}
                      placeholder="Select Bid"
                      showSearch
                      optionFilterProp="label"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Allotted Amount & Allotted Rate — required */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotted Amount"
                        tip="The face value amount being allotted."
                      />
                    }
                    name="allottedAmount"
                    rules={[
                      {
                        required: true,
                        message: "Allotted amount is required",
                      },
                      {
                        type: "number",
                        min: 1,
                        message: "Amount must be greater than 0",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 15,000,000.00"
                      formatter={(v) =>
                        v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                      }
                      parser={(v) => v?.replace(/,/g, "")}
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotted Rate (%)"
                        tip="The rate at which the allotment is made."
                      />
                    }
                    name="allottedRate"
                    rules={[
                      { required: true, message: "Allotted rate is required" },
                      {
                        type: "number",
                        min: 0,
                        message: "Rate must be 0 or greater",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.75"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Allotment Type & Allotment Date — required */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotment Type"
                        tip="Whether the allotment is competitive or non-competitive."
                      />
                    }
                    name="allotmentType"
                    rules={[
                      { required: true, message: "Allotment type is required" },
                    ]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={ALLOTMENT_TYPE_OPTIONS}
                      placeholder="Select Allotment Type"
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotment Date"
                        tip="The date on which the allotment is made."
                      />
                    }
                    name="allotmentDate"
                    rules={[
                      { required: true, message: "Allotment date is required" },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Allotment Yield & Allotment Price — optional */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotment Yield (%) "
                        tip="The yield associated with this allotment."
                      />
                    }
                    name="allotmentYield"
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 12.75"
                      precision={2}
                      min={0}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Allotment Price "
                        tip="The price per 100 face value for this allotment."
                      />
                    }
                    name="allotmentPrice"
                  >
                    <InputNumber
                      style={{ width: "100%", height: 44 }}
                      placeholder="e.g. 98.45"
                      precision={2}
                      min={0}
                      max={100}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Settlement Date — optional */}
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Settlement Date"
                        tip="The date on which the allotment settles."
                      />
                    }
                    name="settlementDate"
                  >
                    <DatePicker
                      style={{ width: "100%", height: 44 }}
                      format="YYYY-MM-DD"
                      placeholder="YYYY-MM-DD"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <div
                style={{ borderTop: "1px solid #EAECF0", margin: "8px 0 20px" }}
              />

              <Row justify="end">
                <Col>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #D0D5DD",
                      background: "#fff",
                      color: "#344054",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                      marginRight: 12,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => form.submit()}
                    style={{
                      height: 44,
                      padding: "0 24px",
                      borderRadius: 8,
                      border: "1px solid #BA24D5",
                      background: "#BA24D5",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    Submit
                  </button>
                </Col>
              </Row>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(
  CreateAuctionAllotment,
);
